import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import {
  clearFamilleDefautData, clearMessages, deleteFamilleDefaut, fetchFamilleDefaut,
  saveFamilleDefaut, setPartageFamille,
} from '../../reduxStore/FamilleDefautSlice';
import { fetchDepartements } from '../../reduxStore/DepartementSlice';
import { FamilleDefaut, Tiers } from '../../models/models';
import SelectDepartementsPartage, { parsePartageIds } from '../Selects/SelectDepartementsPartage';
import { useSnackbar } from '../SnackbarProvider';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import { PERMISSION_PRIX_REVIENT, TypeTiers } from '../../constants/enums';
import { usePermission } from '../../hooks/usePermission';
import { labels } from '../../constants/labels';

interface FamilleDefautDialogProps {
  open: boolean;
  onClose: () => void;
  familleId: number;
  familleNom: string;
}

type ChampsDefaut = Record<
  'prix_v' | 'prix_r' | 'remise' | 'tva' | 'unite' | 'unite_sec' | 'pieces_par_carton' | 'stock_securite' | 'track_stock',
  string
>;

const VIDE: ChampsDefaut = {
  prix_v: '', prix_r: '', remise: '', tva: '', unite: '', unite_sec: '',
  pieces_par_carton: '', stock_securite: '', track_stock: '',
};

const versTexte = (v: any) => (v === null || v === undefined ? '' : String(v));
const versValeur = (v: string) => (v.trim() === '' ? null : v);

/**
 * Valeurs par défaut d'une famille d'articles (migration 0136). Champ vide = rien
 * à proposer. Ces valeurs sont PROPOSÉES à l'attribution de la famille (fiche
 * article, import optionnel, action de masse), jamais imposées.
 */
const FamilleDefautDialog: React.FC<FamilleDefautDialogProps> = ({ open, onClose, familleId, familleNom }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { familleDefautData, loading, error, successMessage } = useSelector((s: RootState) => s.FamilleDefautSlice);
  const { departements } = useSelector((s: RootState) => s.DepartementSlice);
  // Sans le droit de voir le coût (0156) : prix de revient absent (l'API garde la valeur en place)
  const voitCout = usePermission(PERMISSION_PRIX_REVIENT);

  const [defaut, setDefaut] = useState<ChampsDefaut>(VIDE);
  const [partage, setPartage] = useState<number[]>([]);
  // Fournisseur par défaut (0143) — ligne défaut uniquement ; null = rien à proposer
  const [fournisseur, setFournisseur] = useState<Tiers | null>(null);
  const [ligneDept, setLigneDept] = useState<{ departement_id: number | ''; prix_v: string; prix_r: string; remise: string }>(
    { departement_id: '', prix_v: '', prix_r: '', remise: '' }
  );

  useEffect(() => {
    if (!open) return;
    dispatch(fetchDepartements());
    const request = dispatch(fetchFamilleDefaut(familleId));
    return () => request.abort();
  }, [open, familleId, successMessage, dispatch]);

  // Remplit le formulaire quand les données de la famille arrivent
  useEffect(() => {
    if (!familleDefautData || familleDefautData.famille_id !== familleId) return;
    const d = familleDefautData.defaut;
    setDefaut(d ? {
      prix_v: versTexte(d.prix_v), prix_r: versTexte(d.prix_r), remise: versTexte(d.remise), tva: versTexte(d.tva),
      unite: versTexte(d.unite), unite_sec: versTexte(d.unite_sec),
      pieces_par_carton: versTexte(d.pieces_par_carton), stock_securite: versTexte(d.stock_securite),
      track_stock: d.track_stock == null ? '' : (Number(d.track_stock) ? '1' : '0'),
    } : VIDE);
    // Valeur initiale depuis fournisseur_nom (affichée même hors du département courant)
    setFournisseur(d?.fournisseur_id
      ? ({ id: d.fournisseur_id, nom: d.fournisseur_nom ?? `#${d.fournisseur_id}`, prenom: '', type: TypeTiers.FOURNISSEUR } as Tiers)
      : null);
    setPartage(parsePartageIds(familleDefautData.departements_partage_ids));
  }, [familleDefautData, familleId]);

  useEffect(() => {
    if (error) { showSnackbar(error, 'error'); dispatch(clearMessages()); }
    if (successMessage) { showSnackbar(successMessage, 'success'); dispatch(clearMessages()); }
  }, [error, successMessage, showSnackbar, dispatch]);

  const fermer = () => {
    dispatch(clearFamilleDefautData());
    setLigneDept({ departement_id: '', prix_v: '', prix_r: '', remise: '' });
    onClose();
  };

  const enregistrerDefaut = () => {
    const fd: FamilleDefaut = {
      famille_id: familleId,
      departement_id: null,
      prix_v: versValeur(defaut.prix_v) as any,
      prix_r: versValeur(defaut.prix_r) as any,
      remise: versValeur(defaut.remise) as any,
      tva: versValeur(defaut.tva) as any,
      unite: versValeur(defaut.unite),
      unite_sec: versValeur(defaut.unite_sec),
      pieces_par_carton: versValeur(defaut.pieces_par_carton) as any,
      stock_securite: versValeur(defaut.stock_securite) as any,
      track_stock: defaut.track_stock === '' ? null : defaut.track_stock === '1',
      fournisseur_id: fournisseur?.id ?? null,
    };
    dispatch(saveFamilleDefaut(fd));
  };

  const enregistrerPrixDept = async () => {
    if (ligneDept.departement_id === '') return;
    const res = await dispatch(saveFamilleDefaut({
      famille_id: familleId,
      departement_id: Number(ligneDept.departement_id),
      prix_v: versValeur(ligneDept.prix_v) as any,
      prix_r: versValeur(ligneDept.prix_r) as any,
      remise: versValeur(ligneDept.remise) as any,
    }));
    if (saveFamilleDefaut.fulfilled.match(res)) setLigneDept({ departement_id: '', prix_v: '', prix_r: '', remise: '' });
  };

  const modifierPrixDept = (fd: FamilleDefaut) =>
    setLigneDept({
      departement_id: Number(fd.departement_id),
      prix_v: versTexte(fd.prix_v), prix_r: versTexte(fd.prix_r), remise: versTexte(fd.remise),
    });

  const champ = (key: keyof ChampsDefaut, label: string, type: 'number' | 'text' = 'number') => (
    <TextField
      size="small" fullWidth type={type} label={label}
      value={defaut[key]}
      onChange={(e) => setDefaut((p) => ({ ...p, [key]: e.target.value }))}
    />
  );

  return (
    <Dialog open={open} onClose={fermer} maxWidth="md" fullWidth>
      <DialogTitle>Valeurs par défaut — {familleNom}</DialogTitle>
      <DialogContent dividers>
        {loading && !familleDefautData ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
              Proposées quand un article est rangé dans cette famille. Champ vide = rien à proposer.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Valeurs par défaut</Typography>
            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(min(100%, 180px), 1fr))" gap={2}>
              {champ('prix_v', 'Prix de vente')}
              {voitCout && champ('prix_r', 'Prix de revient')}
              {champ('remise', 'Remise max (%)')}
              {champ('tva', 'TVA (%)')}
              {champ('unite', 'Unité', 'text')}
              {champ('unite_sec', 'Unité secondaire', 'text')}
              {champ('pieces_par_carton', 'Pièces par carton')}
              {champ('stock_securite', 'Stock de sécurité')}
              <FormControl fullWidth size="small">
                <InputLabel>Suivi du stock</InputLabel>
                <Select
                  label="Suivi du stock" value={defaut.track_stock}
                  onChange={(e) => setDefaut((p) => ({ ...p, track_stock: String(e.target.value) }))}
                >
                  <MenuItem value=""><em>Non défini</em></MenuItem>
                  <MenuItem value="1">Oui</MenuItem>
                  <MenuItem value="0">Non</MenuItem>
                </Select>
              </FormControl>
              <FournisseurAutoC
                selectedFournisseur={fournisseur}
                onFournisseurChange={setFournisseur}
                label={labels.fournisseurParDefaut}
              />
            </Box>
            <Box mt={1.5}>
              <Button variant="contained" size="small" onClick={enregistrerDefaut}>Enregistrer les valeurs</Button>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Prix par département</Typography>
            {(familleDefautData?.prix_departements ?? []).length === 0 ? (
              <Typography variant="body2" sx={{ color: '#667085', mb: 1 }}>Aucun prix département.</Typography>
            ) : (
              (familleDefautData?.prix_departements ?? []).map((fd) => (
                <Box
                  key={fd.id} display="flex" alignItems="center" justifyContent="space-between"
                  sx={{ py: 0.5, px: 1, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f4f5f7' } }}
                  onClick={() => modifierPrixDept(fd)}
                >
                  <Typography variant="body2">
                    <strong>{fd.departement_nom}</strong> — vente {versTexte(fd.prix_v) || '—'}
                    {voitCout && <> · revient {versTexte(fd.prix_r) || '—'}</>} · remise {versTexte(fd.remise) || '—'}
                  </Typography>
                  <Tooltip title="Supprimer">
                    <IconButton
                      size="small" color="error"
                      onClick={(e) => { e.stopPropagation(); fd.id && dispatch(deleteFamilleDefaut(fd.id)); }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))
            )}
            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(min(100%, 160px), 1fr))" gap={2} mt={1}>
              <FormControl fullWidth size="small">
                <InputLabel>Département</InputLabel>
                <Select
                  label="Département" value={ligneDept.departement_id}
                  onChange={(e) => setLigneDept((p) => ({ ...p, departement_id: e.target.value as number }))}
                >
                  {departements.map((d) => <MenuItem key={d.id} value={d.id}>{d.nom}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" type="number" label="Prix de vente" value={ligneDept.prix_v}
                onChange={(e) => setLigneDept((p) => ({ ...p, prix_v: e.target.value }))} />
              {voitCout && (
                <TextField size="small" type="number" label="Prix de revient" value={ligneDept.prix_r}
                  onChange={(e) => setLigneDept((p) => ({ ...p, prix_r: e.target.value }))} />
              )}
              <TextField size="small" type="number" label="Remise max (%)" value={ligneDept.remise}
                onChange={(e) => setLigneDept((p) => ({ ...p, remise: e.target.value }))} />
              <Button variant="outlined" size="small" disabled={ligneDept.departement_id === ''} onClick={enregistrerPrixDept}>
                Enregistrer le prix
              </Button>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Partage par défaut</Typography>
            <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
              <Box sx={{ flex: '1 1 260px' }}>
                <SelectDepartementsPartage value={partage} onChange={setPartage} />
              </Box>
              <Button
                variant="outlined" size="small" sx={{ mt: 0.5 }}
                onClick={() => dispatch(setPartageFamille({ famille_id: familleId, departements: partage }))}
              >
                Enregistrer le partage
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={fermer}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FamilleDefautDialog;
